import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import ProfilePicture from 'components/profile/profile-picture'
import Icon from 'components/icon/icon'
import DashboardTasksStatus from './dashboard-tasks-status'

import SingleTaskPortal from 'sections/projects/project/project-tasks/single-task/single-task-portal'

import moment from 'moment'

import {
    isMobile,
    getPrimaryColumnValue,
    getApplicableUsers,
} from 'utils'

export default ({
    isTaskCurrentlyTracking,
    task,
    project,
    milestone,
    openModal,
    addTaskToTodos,
    context,
    startTiming,
    openItem,
    statusRefs,
    openStatusModal,
    getStatusName,
    clearActiveTask,
    type,
    isTaskInTodos,
    userData,
    allTodoDays,
    allTodos,
    allTeams,
    toggleState,
    activeTask,
    updateTask,
    index,
    taskObjects,
    allUsers,
    isOpen,
    closeTask,
    getUrl
}) => {
    const _isMobile = isMobile()

    let isInTodos = false
    let isCurrentlyTracking = false

    if (type !== 'reporting') {
        isInTodos = isTaskInTodos(task, userData.todo_days, allTodoDays, allTodos)
        isCurrentlyTracking = isTaskCurrentlyTracking(task)
    }

    const addToTodoMessage = isInTodos ? 'Already in to-do' : 'Add to my to-do'

    const applicableUsers = useMemo(() => {
        return getApplicableUsers(context.account, allUsers, project, allTeams)
    }, [context.account, allUsers, project, allTeams])

    let endDateMessage = ''
    let isOverdue = false

    if (!toggleState) {
        const dates = getPrimaryColumnValue('date', task, project)
        const daysBetween = moment(dates.end).endOf('day').diff(moment().endOf('day'), 'days')

        if (daysBetween > 1) {
            endDateMessage = 'In ' + daysBetween + ' days'
        } else if (daysBetween == 0) {
            endDateMessage = 'Today'
        } else if (daysBetween == 1) {
            endDateMessage = 'Tomorrow'
        } else {
            isOverdue = true

            const daysAgo = (daysBetween * -1)

            if (daysAgo == 1) {
                endDateMessage = 'Yesterday'
            } else if (isNaN(daysAgo)) {
                endDateMessage = 'No end date'
            } else {
                endDateMessage = daysAgo + ' days ago'
            }
        }
    } else {
        const daysBetween = moment().diff(moment(task.created_at), 'days')

        if (daysBetween == 0) {
            endDateMessage = 'Added today'
        } else {
            if (daysBetween == 1) {
                endDateMessage = 'Yesterday'
            } else {
                endDateMessage = daysBetween + ' days ago'
            }
        }
    }

    const metaButtons = type === 'reporting' ? [] : [
        {
            icon: 'bell-slash-o',
            disabled: false,
            onClick: e => {
                e.stopPropagation()
                openModal('dismiss', 'task', { task })
            },
            tooltip: 'Dismiss'
        }, {
            icon: 'check-square-o',
            disabled: isInTodos,
            onClick: e => {
                e.stopPropagation()
                addTaskToTodos(isInTodos, task, project)
            },
            tooltip: addToTodoMessage
        }
    ]

    if (context.account.isPremium && project.has_timing && !_isMobile) {
        metaButtons.unshift({
            icon: 'fe-clock',
            disabled: isCurrentlyTracking,
            onClick: e => {
                e.stopPropagation()
                startTiming(task)
            },
            tooltip: isCurrentlyTracking ? "Already tracking task" : "Start time tracking"
        })
    }

    const taskUsers = getPrimaryColumnValue('people', task, project)
    const statusId = getPrimaryColumnValue('status', task, project)

    let statusIconClassName = 'status-icon'

    if (statusId) {
        statusIconClassName += ' colour-id-' + statusId
    } else {
        statusIconClassName += ' colour-id-null'
    }

    let className = 'list-item'

    const isStatusPopMenuOpen = (activeTask && activeTask.id == task.id)

    if (isStatusPopMenuOpen) {
        className += ' focused'
    }

    const innerJsx = (
        <div className="list-item-content">
            <h4>
                <em>
                    {task.is_quick_task &&
                        <span className={'tooltip-trigger quick-task'}>
                            <Icon name={'bolt'} />
                            <div className="tooltip">
                                Quick task
                        </div>
                        </span>
                    }
                    {task.name}
                </em>
                <span>
                    <span onClick={e => {
                        e.stopPropagation()
                        openItem('project', project)
                    }}>
                        {project.name} &bull; {milestone.name}
                    </span>
                    {!_isMobile ?
                        <span
                            className="status"
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                openStatusModal(task, isStatusPopMenuOpen)
                            }}
                            ref={ref => statusRefs[task.id] = ref}
                        >
                            <em className={statusIconClassName} />
                            {getStatusName(statusId, project)}
                            <DashboardTasksStatus
                                task={task}
                                project={project}
                                statusId={statusId}
                                isOpen={isStatusPopMenuOpen}
                                closeMenu={clearActiveTask}
                                updateTask={updateTask}
                                shouldGoUp={index > 1 && taskObjects.length - index < 4}
                                outerRef={statusRefs[task.id]}
                            />
                        </span>
                        : null}
                    {(type == 'reporting' && taskUsers.length) ?
                        <ul className="users-list">
                            {taskUsers.map(userId => {
                                const user = allUsers[userId]

                                if (user) {
                                    return (
                                        <li
                                            key={userId}
                                            className="tooltip-trigger list-item"
                                        >
                                            <ProfilePicture user={user} />
                                            <div className="tooltip">
                                                {user.first_name + ' ' + user.last_name}
                                            </div>
                                        </li>
                                    )
                                }
                            })}
                        </ul>
                        : null}
                </span>
            </h4>
            <div
                className="meta"
            >
                {metaButtons.map((button, index) => {
                    let className = 'tooltip-trigger'

                    if (button.disabled) {
                        className += ' disabled'
                    }

                    return (
                        <button
                            key={index}
                            className={className}
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                button.onClick(e)
                            }}
                        >
                            <Icon name={button.icon} />
                            <div className="tooltip">
                                {button.tooltip}
                            </div>
                        </button>
                    )
                })}
                <span className={isOverdue ? 'alert' : null}>
                    <Icon name={isOverdue ? 'fe-alert-triangle' : "fe-calendar"} />
                    <span>
                        {endDateMessage}
                    </span>
                </span>
            </div>
        </div>
    )

    return (
        <li
            className={className}
            key={task.id}
        >
            { _isMobile ?
                <Link to={getUrl(task)}>
                    { innerJsx}
                </Link>
            :
                <a onClick={() => openItem('task', project, task)}>
                    {innerJsx}
                </a>
            }
            { isOpen &&
                <SingleTaskPortal
                    project={project}
                    applicableUsers={applicableUsers}
                    taskId={task.id}
                    close={closeTask}
                />
            }
        </li>
    )
}